import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Zap,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { cn } from '@/lib/tw-merge/cn';
import { type OperationCard } from '@/types/meet-live-ai';

import { OPERATION_CARDS } from '../data/operations-data';

type OperationCardOffset = -2 | -1 | 0 | 1 | 2;

type OperationCardLayout = {
  x: number;
  zIndex: number;
  opacity: number;
  width: number;
  height: number;
  borderRadius: number;
  padding: number;
  borderWidth: number;
  marginTop: number;
  marginLeft: number;
};

const OPERATION_CARD_LAYOUTS: Record<OperationCardOffset, OperationCardLayout> =
  {
    [-2]: {
      x: -440,
      zIndex: 20,
      opacity: 0.45,
      width: 199.48,
      height: 288,
      borderRadius: 18.32,
      padding: 21.37,
      borderWidth: 0.76,
      marginTop: -144,
      marginLeft: -99.74,
    },
    [-1]: {
      x: -250,
      zIndex: 30,
      opacity: 0.85,
      width: 250.34,
      height: 288,
      borderRadius: 18.32,
      padding: 21.37,
      borderWidth: 0.76,
      marginTop: -144,
      marginLeft: -125.17,
    },
    [0]: {
      x: 0,
      zIndex: 40,
      opacity: 1,
      width: 320,
      height: 340,
      borderRadius: 24,
      padding: 24,
      borderWidth: 1,
      marginTop: -170,
      marginLeft: -160,
    },
    [1]: {
      x: 250,
      zIndex: 30,
      opacity: 0.85,
      width: 250.34,
      height: 288,
      borderRadius: 18.32,
      padding: 21.37,
      borderWidth: 0.76,
      marginTop: -144,
      marginLeft: -125.17,
    },
    [2]: {
      x: 440,
      zIndex: 20,
      opacity: 0.45,
      width: 199.48,
      height: 288,
      borderRadius: 18.32,
      padding: 21.37,
      borderWidth: 0.76,
      marginTop: -144,
      marginLeft: -99.74,
    },
  };

export default function OperationSection() {
  const [activeIndex, setActiveIndex] = useState(2); // Start at index 2 (Origination Operations, card #3)
  const total = OPERATION_CARDS.length;

  const handleNext = useCallback(() => {
    setActiveIndex(prev => (prev + 1) % total);
  }, [total]);

  const handlePrev = useCallback(() => {
    setActiveIndex(prev => (prev - 1 + total) % total);
  }, [total]);

  // Keyboard navigation for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev]);

  const getCardIcon = (
    iconType: OperationCard['iconType'],
    isActive: boolean
  ) => {
    switch (iconType) {
      case 'origination':
        return (
          <TrendingUp
            className={cn('h-5 w-5', isActive ? 'text-white' : 'text-primary')}
          />
        );
      case 'underwriting':
        return (
          <ShieldCheck
            className={cn('h-5 w-5', isActive ? 'text-white' : 'text-primary')}
          />
        );
      case 'recovery':
        return (
          <RefreshCw
            className={cn('h-4 w-4', isActive ? 'text-white' : 'text-primary')}
          />
        );
      case 'admin':
        return (
          <Sparkles
            className={cn('h-4 w-4', isActive ? 'text-white' : 'text-primary')}
          />
        );
      case 'enablement':
        return (
          <Zap
            className={cn('h-4 w-4', isActive ? 'text-white' : 'text-primary')}
          />
        );
      default:
        return (
          <Building2
            className={cn('h-4 w-4', isActive ? 'text-white' : 'text-primary')}
          />
        );
    }
  };

  const getOffset = (index: number): OperationCardOffset =>
    (((index - activeIndex + total + Math.floor(total / 2)) % total) -
      Math.floor(total / 2)) as OperationCardOffset;

  return (
    <section
      id="operations-section"
      className="relative w-full overflow-hidden bg-[#F7F4EF] py-[64px]"
    >
      <div className="app-container flex flex-col items-center">
        {/* Section Heading */}
        <div className="smx-auto mb-10 max-w-[742px] text-center">
          <h1 className="text-center font-fraunces text-2xl font-normal leading-[28px] tracking-[-0.96px] text-foreground sm:text-4xl sm:leading-[48px] lg:text-[48px] lg:leading-[56px]">
            Chinobi leads the AI agents that move your business forward.
          </h1>
        </div>

        {/* Desktop & Tablet Carousel Stage */}
        <div className="relative hidden h-[450px] w-full items-center justify-center overflow-visible md:flex">
          <div className="relative flex h-[340px] w-[320px] items-center justify-center">
            {OPERATION_CARDS.map((card, index) => {
              const offset = getOffset(index);
              const isActive = offset === 0;
              const layout = OPERATION_CARD_LAYOUTS[offset];

              return (
                <motion.div
                  key={card.id}
                  id={`operation-card-${card.id}`}
                  onClick={() => setActiveIndex(index)}
                  animate={{
                    ...layout,
                    backgroundColor: isActive
                      ? '#124B68'
                      : 'rgba(255, 255, 255, 0.88)',
                    borderColor: isActive
                      ? '#182F8B'
                      : 'rgba(255, 255, 255, 0.95)',
                    boxShadow: isActive
                      ? ''
                      : '0px 18.32px 30px -10px rgba(40, 25, 12, 0.18), 0px 8px 18px -4px rgba(0, 0, 0, 0.05), inset 0px 0.76px 0px 0px #FFFFFFE5',
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 260,
                    damping: 26,
                    mass: 0.8,
                  }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    borderStyle: 'solid',
                    backdropFilter: isActive ? 'none' : 'blur(12px)',
                    cursor: isActive ? 'default' : 'pointer',
                  }}
                  className="flex flex-col justify-between overflow-hidden"
                >
                  {/* Card Header: Icon & Number */}
                  <div className="flex w-full items-center justify-between">
                    {isActive ? (
                      <motion.div
                        layout
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.25 }}
                        className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FA551E] shadow-md shadow-orange-950/20"
                      >
                        {getCardIcon(card.iconType, true)}
                      </motion.div>
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FDEEE9]">
                        <div className="h-2.5 w-2.5 rotate-45 transform rounded-sm bg-[#FA551E]" />
                      </div>
                    )}

                    <span
                      className={cn(
                        'text-xs font-semibold tracking-wider transition-colors duration-300',
                        isActive ? 'text-stroke-100' : 'text-[#6F675E]'
                      )}
                    >
                      {card.number}
                    </span>
                  </div>

                  {/* Card Content: Title & Description */}
                  <div className="my-auto py-1">
                    <motion.h3
                      layout="position"
                      className={cn(
                        'mb-2 font-fraunces font-normal transition-colors duration-300',
                        isActive
                          ? 'text-2xl leading-[30px] tracking-[-0.6px] text-white'
                          : 'text-[20.8px] leading-[26px] text-foreground'
                      )}
                    >
                      {card.title}
                    </motion.h3>
                    <motion.p
                      layout="position"
                      className={cn(
                        'max-w-[220px] transition-colors duration-300',
                        isActive
                          ? 'text-sm leading-[21.13px] text-stroke-100'
                          : 'text-[11px] leading-4 text-[#6F675E]'
                      )}
                    >
                      {card.description}
                    </motion.p>
                  </div>

                  {/* Card Footer: Avatars & Collaborative Connectors */}
                  <div className="border-white/10 flex items-center justify-between">
                    <motion.div
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.25 }}
                      className="flex w-full items-center justify-between"
                    >
                      {/* Lead Avatar */}
                      <img
                        src={card.leadAvatar}
                        alt="Lead Agent"
                        referrerPolicy="no-referrer"
                        className={cn(
                          'h-[44px] w-[42px] rounded-xl bg-[#EBEAD8] object-cover',
                          !isActive && 'opacity-60 grayscale'
                        )}
                      />

                      {/* Dashed Connector with Activity Dot */}
                      {isActive && (
                        <div className="relative mx-3 flex flex-1 items-center justify-center">
                          <div className="w-full border-t border-dashed border-sky-300/40" />
                          <div className="absolute h-2 w-2 rounded-full bg-primary ring-4 ring-[#124B68]" />
                        </div>
                      )}
                      {/* Team Avatars Container */}
                      <div className="flex items-center -space-x-2.5">
                        {card.teamAvatars.map((avatar, idx) => (
                          <img
                            key={idx}
                            src={avatar}
                            alt="Team Agent"
                            referrerPolicy="no-referrer"
                            className={cn(
                              'h-[44px] w-[42px] rounded-xl object-cover',
                              !isActive && 'opacity-60 grayscale'
                            )}
                          />
                        ))}
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile View: Vertical Stacked Cards (shown on mobile < md) */}
        <div className="my-2 flex w-full max-w-[420px] flex-col gap-4 md:hidden">
          {OPERATION_CARDS.map((card, index) => {
            const isActive = index === activeIndex;

            return (
              <motion.div
                key={card.id}
                id={`mobile-operation-card-${card.id}`}
                onClick={() => setActiveIndex(index)}
                layout
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 28,
                }}
                className={cn(
                  'w-full cursor-pointer rounded-[24px] p-5 transition-all duration-300',
                  isActive
                    ? 'border border-[#182F8B] bg-[#124B68] text-white shadow-xl shadow-[#124B68]/30'
                    : 'border border-neutral-200/70 bg-white text-[#1A1816] shadow-sm hover:shadow-md'
                )}
              >
                {/* Header: Icon & Number */}
                <div className="mb-3.5 flex w-full items-center justify-between">
                  {isActive ? (
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FA551E] shadow-md shadow-orange-950/20">
                      {getCardIcon(card.iconType, true)}
                    </div>
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FDEEE9]">
                      <div className="h-2.5 w-2.5 rotate-45 transform rounded-sm bg-[#FA551E]" />
                    </div>
                  )}

                  <span
                    className={cn(
                      'font-mono text-xs font-semibold tracking-wider',
                      isActive ? 'text-white/60' : 'text-neutral-400'
                    )}
                  >
                    {card.number}
                  </span>
                </div>

                {/* Content: Title & Description */}
                <div className="mb-4">
                  <h3
                    className={cn(
                      'mb-1.5 text-lg font-semibold leading-snug tracking-tight',
                      isActive ? 'text-white' : 'text-[#1A1816]'
                    )}
                  >
                    {card.title}
                  </h3>
                  <p
                    className={cn(
                      'text-xs leading-relaxed',
                      isActive ? 'text-[#A8C9DC]' : 'text-neutral-500'
                    )}
                  >
                    {card.description}
                  </p>
                </div>

                {/* Footer: Avatars */}
                <div className="border-white/10 flex items-center justify-between border-t pt-3">
                  {isActive ? (
                    <div className="flex w-full items-center justify-between">
                      {/* Lead Avatar */}
                      <div className="group relative">
                        <img
                          src={card.leadAvatar}
                          alt="Lead Agent"
                          referrerPolicy="no-referrer"
                          style={{
                            width: '42px',
                            height: '44px',
                            borderRadius: '12px',
                            borderWidth: '0.2px',
                            borderColor: 'rgba(255, 255, 255, 0.6)',
                            borderStyle: 'solid',
                          }}
                          className="bg-[#EBEAD8] object-cover shadow-sm"
                        />
                        <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border border-[#124B68] bg-emerald-400" />
                      </div>

                      {/* Dashed Connector with Activity Dot */}
                      <div className="relative mx-3 flex flex-1 items-center justify-center">
                        <div className="w-full border-t border-dashed border-sky-300/40" />
                        <div className="absolute h-2 w-2 rounded-full bg-[#FA551E] ring-4 ring-[#124B68]" />
                      </div>

                      {/* Team Avatars Container */}
                      <div className="flex items-center -space-x-2.5 rounded-[14px] border border-sky-400/20 bg-[#0C3B53]/80 p-1 shadow-inner">
                        {card.teamAvatars.map((avatar, idx) => (
                          <img
                            key={idx}
                            src={avatar}
                            alt="Team Agent"
                            referrerPolicy="no-referrer"
                            style={{
                              width: '38px',
                              height: '40px',
                              borderRadius: '10px',
                              borderWidth: '0.2px',
                              borderColor: 'rgba(255, 255, 255, 0.4)',
                              borderStyle: 'solid',
                            }}
                            className="object-cover"
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full items-center justify-between">
                      <img
                        src={card.leadAvatar}
                        alt="Lead Agent"
                        referrerPolicy="no-referrer"
                        style={{
                          width: '32px',
                          height: '34px',
                          borderRadius: '8px',
                          borderWidth: '0.2px',
                          borderColor: 'rgba(0, 0, 0, 0.15)',
                          borderStyle: 'solid',
                        }}
                        className="bg-[#EBEAD8] object-cover opacity-60 grayscale"
                      />
                      <div className="flex items-center -space-x-2 opacity-60 grayscale">
                        {card.teamAvatars.map((avatar, idx) => (
                          <img
                            key={idx}
                            src={avatar}
                            alt="Team Agent"
                            referrerPolicy="no-referrer"
                            style={{
                              width: '30px',
                              height: '32px',
                              borderRadius: '8px',
                              borderWidth: '0.2px',
                              borderColor: 'rgba(0, 0, 0, 0.15)',
                              borderStyle: 'solid',
                            }}
                            className="object-cover"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Carousel Navigation Controls: Prev, Dots, Next (shown on tablet & desktop) */}
        <div className="mt-10 hidden items-center gap-5 md:flex">
          {/* Previous Button */}
          <button
            id="operation-carousel-prev"
            onClick={handlePrev}
            aria-label="Previous card position"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-neutral-200/90 bg-white text-neutral-600 shadow-sm transition-all hover:border-neutral-400 hover:text-neutral-950 hover:shadow active:scale-95"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          {/* Indicator Dots */}
          <div
            className="flex items-center gap-1.5"
            role="tablist"
            aria-label="Operation cards"
          >
            {OPERATION_CARDS.map((card, idx) => {
              const isActive = idx === activeIndex;
              return (
                <button
                  key={card.id}
                  id={`operation-dot-${card.id}`}
                  onClick={() => setActiveIndex(idx)}
                  role="tab"
                  aria-selected={isActive}
                  aria-label={`Go to ${card.title}`}
                  className={cn(
                    'h-2 cursor-pointer rounded-full transition-all duration-300',
                    isActive
                      ? 'w-7 bg-primary'
                      : 'w-2 bg-neutral-300 hover:bg-neutral-400'
                  )}
                />
              );
            })}
          </div>

          {/* Next Button */}
          <button
            id="operation-carousel-next"
            onClick={handleNext}
            aria-label="Next card position"
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-neutral-200/90 bg-white text-neutral-600 shadow-sm transition-all hover:border-neutral-400 hover:text-neutral-950 hover:shadow active:scale-95"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
